import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, AlertTriangle, Info, X } from 'lucide-react';
import { errorMessage, isSilentError } from '../services/feedbackMessages';

// ── Notificaciones "toast" ──────────────────────────────────────────────────
// Confirmación no bloqueante de cada acción que manda o modifica datos
// ("Entrada registrada a las 09:03", "Solicitud enviada. Pendiente de
// aprobación."). Se monta UNA vez en la raíz (App.js) y cualquier página o
// modal la usa con:
//
//   const toast = useToast();
//   toast.success('Sede creada.');
//   toast.error(err, 'No se pudo guardar la sede.');   // Error o texto
//   toast.info('…'); toast.warning('…'); toast.dismiss(id);
//
// Duración: éxito/info 4 s; aviso 6 s; error 8 s (y siempre se pueden cerrar
// con la X). `duration: 0` → no se cierra solo. Los textos viven en
// src/services/feedbackMessages.js, no en las páginas.
//
// Accesibilidad: el contenedor es una región aria-live="polite"; los errores
// llevan role="alert" (se anuncian de inmediato) y el resto role="status".

export const TOAST_DURATIONS = {
  success: 4000,
  info: 4000,
  warning: 6000,
  error: 8000,
};

// Nunca se amontonan más de estas: la más antigua sale al entrar una nueva.
const MAX_TOASTS = 4;

const noop = () => null;
// Fuera del provider (tests de componentes sueltos, storybook…) useToast
// devuelve esta API vacía en vez de romper.
const NOOP_API = { show: noop, success: noop, error: noop, info: noop, warning: noop, dismiss: noop };

const ToastContext = createContext(null);

export const useToast = () => useContext(ToastContext) || NOOP_API;

const STYLES = {
  success: { Icon: CheckCircle2, bar: 'bg-success', icon: 'text-success' },
  info: { Icon: Info, bar: 'bg-primary', icon: 'text-primary-dark' },
  warning: { Icon: AlertTriangle, bar: 'bg-warning', icon: 'text-warning' },
  error: { Icon: AlertTriangle, bar: 'bg-danger', icon: 'text-danger' },
};

const ToastItem = ({ toast, onDismiss }) => {
  const { Icon, bar, icon } = STYLES[toast.type] || STYLES.info;
  const isError = toast.type === 'error';
  return (
    <div
      role={isError ? 'alert' : 'status'}
      data-toast-type={toast.type}
      className="pointer-events-auto relative overflow-hidden w-full sm:w-96 bg-white border border-slate-200 rounded-xl shadow-lg flex items-start gap-3 pl-4 pr-2 py-3"
    >
      <span className={`absolute left-0 top-0 h-full w-1 ${bar}`} aria-hidden="true" />
      <Icon size={18} className={`${icon} shrink-0 mt-0.5`} aria-hidden="true" />
      <div className="flex-1 min-w-0 text-sm">
        {toast.title && <div className="font-semibold text-ink">{toast.title}</div>}
        <div className={toast.title ? 'text-ink-muted' : 'text-ink'}>{toast.message}</div>
      </div>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        className="text-ink-muted hover:text-ink p-1 rounded-lg hover:bg-slate-100 transition-colors shrink-0"
        aria-label="Cerrar notificación"
      >
        <X size={14} />
      </button>
    </div>
  );
};

export const ToastProvider = ({ children, durations = TOAST_DURATIONS }) => {
  const [toasts, setToasts] = useState([]);
  const timers = useRef(new Map());
  const seq = useRef(0);

  const dismiss = useCallback((id) => {
    const t = timers.current.get(id);
    if (t) { clearTimeout(t); timers.current.delete(id); }
    setToasts((list) => list.filter((x) => x.id !== id));
  }, []);

  const show = useCallback((type, message, opts = {}) => {
    if (message == null || message === '') return null;
    seq.current += 1;
    const id = `t${seq.current}`;
    const duration = opts.duration != null ? opts.duration : (durations[type] ?? TOAST_DURATIONS.info);
    setToasts((list) => {
      const next = [...list, { id, type, message: String(message), title: opts.title || null }];
      // Las que salen por exceso también pierden su temporizador.
      while (next.length > MAX_TOASTS) {
        const dropped = next.shift();
        const t = timers.current.get(dropped.id);
        if (t) { clearTimeout(t); timers.current.delete(dropped.id); }
      }
      return next;
    });
    if (duration > 0) {
      timers.current.set(id, setTimeout(() => dismiss(id), duration));
    }
    return id;
  }, [dismiss, durations]);

  useEffect(() => {
    const map = timers.current;
    return () => { map.forEach((t) => clearTimeout(t)); map.clear(); };
  }, []);

  const api = useMemo(() => ({
    show,
    success: (message, opts) => show('success', message, opts),
    info: (message, opts) => show('info', message, opts),
    warning: (message, opts) => show('warning', message, opts),
    // Acepta un Error (ApiError ya trae el mensaje traducido al castellano) o
    // un texto. `fallback` se usa si el error no trae mensaje. Las sesiones
    // caducadas no se notifican aquí: AuthContext ya cierra y lo explica.
    error: (errOrMessage, fallback, opts) => {
      if (isSilentError(errOrMessage)) return null;
      const message = typeof errOrMessage === 'string'
        ? errOrMessage
        : errorMessage(errOrMessage, fallback);
      return show('error', message, opts);
    },
    dismiss,
  }), [show, dismiss]);

  // Posición: móvil arriba a lo ancho; escritorio abajo a la derecha, por
  // ENCIMA del botón flotante de mensajería (bottom-6, 56 px).
  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        aria-label="Notificaciones"
        className="pointer-events-none fixed z-[70] inset-x-4 top-4 flex flex-col items-stretch gap-2 sm:inset-x-auto sm:top-auto sm:bottom-24 sm:right-6 sm:items-end"
      >
        {toasts.map((t) => <ToastItem key={t.id} toast={t} onDismiss={dismiss} />)}
      </div>
    </ToastContext.Provider>
  );
};

export default ToastProvider;
